import React from 'react'
import { getDay } from 'date-fns'
import type { UseQueryResult } from '@tanstack/react-query'
import type { WeeksSummary, WeeksSummaryParams } from '../api/missions'
import { WorkPeriodWithSummary } from '../api/work-periods'
import { getDocIdAsString } from '../utils/documents'
import { getWeekdayMondayBased, type WeekDay } from '../utils/date'
import { useWeeksSummaryQuery } from '../queries/missions'
import type { QueryOptions } from '../queries'
import {
  SummaryData,
  SummaryDayData,
} from '../../../backend/src/services/resources/period-summaries/period-summary.types'
import { WeekSummaryEmployerRule } from '../../../backend/src/services/resources/missions/missions.summary.service.types'

export interface ExtractWorkPeriodsFromWeeksSummaryResult {
  workPeriods: WorkPeriodWithSummary[]
  employerRules: WeekSummaryEmployerRule[]
  weekTotalsByWorkPeriodId: Map<string, SummaryData>
}

/**
 * Flattens {@link WeeksSummary} into work periods and week-scoped totals for employer-rule columns.
 *
 * If `missionIdForExtraction` is set, only work periods for that mission are collected (same as the
 * mission details table). If omitted, work periods from **all** missions in the payload are merged
 * (caller must ensure API params match the intended scope). Aggregated employer rules may mix rule
 * sets when several employers appear — use a mission-scoped API filter when you need a single context.
 */
export function extractWorkPeriodsFromWeeksSummary(
  weeksSummary: WeeksSummary | undefined,
  missionIdForExtraction?: string,
): ExtractWorkPeriodsFromWeeksSummaryResult {
  const empty: ExtractWorkPeriodsFromWeeksSummaryResult = {
    workPeriods: [],
    employerRules: [],
    weekTotalsByWorkPeriodId: new Map<string, SummaryData>(),
  }
  if (!weeksSummary?.length) return empty

  const missionIdStr = missionIdForExtraction ? getDocIdAsString(missionIdForExtraction) : null
  const wpList: WorkPeriodWithSummary[] = []
  const weekTotalsByWorkPeriodId = new Map<string, SummaryData>()
  const seenRuleIds = new Set<string>()
  const employerRules: WeekSummaryEmployerRule[] = []

  for (const userEntry of weeksSummary) {
    for (const week of userEntry.weeks) {
      const missionsSlice = missionIdStr
        ? week.missions.filter(m => getDocIdAsString(m._id) === missionIdStr)
        : week.missions

      for (const m of missionsSlice) {
        for (const wp of m.workPeriods) {
          wpList.push(wp as unknown as WorkPeriodWithSummary)
          weekTotalsByWorkPeriodId.set(getDocIdAsString(wp._id), week.totals)
        }
      }

      for (const r of week.employerRules) {
        const rid = getDocIdAsString(r.ruleId)
        if (seenRuleIds.has(rid)) continue
        seenRuleIds.add(rid)
        if (r.display?.summary?.days) {
          employerRules.push(r)
        }
      }
    }
  }

  wpList.sort((a, b) => new Date(a.start.date).getTime() - new Date(b.start.date).getTime())

  return { workPeriods: wpList, employerRules, weekTotalsByWorkPeriodId }
}

export interface UseWeeksSummaryWorkPeriodsTableOptions extends QueryOptions<WeeksSummary> {
  /** When false, the weeks-summary request is skipped */
  enabled: boolean
  /**
   * When set, only work periods for this mission are kept. When omitted, all missions in the response
   * contribute work periods.
   */
  missionId?: string
}

/**
 * Runs {@link useWeeksSummaryQuery} with caller-built `WeeksSummaryParams`, then derives data for
 * {@link DailyWorkPeriodsTable}: work periods, employer rules, and `resolveEmployerRuleDay`.
 */
export function useWeeksSummaryWorkPeriodsTable(
  weeksSummaryParams: WeeksSummaryParams | null | undefined,
  { enabled, missionId, ...queryOptions }: UseWeeksSummaryWorkPeriodsTableOptions,
): {
  workPeriods: WorkPeriodWithSummary[]
  employerRules: WeekSummaryEmployerRule[]
  resolveEmployerRuleDay: (wp: WorkPeriodWithSummary) => SummaryDayData | undefined
} & Pick<UseQueryResult<WeeksSummary>, 'data' | 'isLoading' | 'isFetching' | 'isError' | 'error'> {
  const query = useWeeksSummaryQuery(weeksSummaryParams ?? { from: new Date(), to: new Date() }, {
    ...queryOptions,
    enabled: Boolean(enabled && weeksSummaryParams),
  })

  const { workPeriods, employerRules, weekTotalsByWorkPeriodId } = React.useMemo(
    () => extractWorkPeriodsFromWeeksSummary(query.data, missionId),
    [query.data, missionId],
  )

  const resolveEmployerRuleDay = React.useCallback(
    (wp: WorkPeriodWithSummary) => {
      const totals = weekTotalsByWorkPeriodId.get(getDocIdAsString(wp._id))
      if (!totals) return undefined
      const weekDayNumber = getWeekdayMondayBased(getDay(new Date(wp.start.date)) as WeekDay)
      return totals.days[weekDayNumber]
    },
    [weekTotalsByWorkPeriodId],
  )

  return {
    workPeriods,
    employerRules,
    resolveEmployerRuleDay,
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  }
}
